/**
 * useCancelCredit
 *
 * Hook de mutação para cancelar manualmente um crédito ativo do cliente via RPC
 * `cancel_financeiro_credito`. O crédito permanece no histórico (auditoria),
 * porém com status `canceled` e saldo zerado — deixando de ser utilizável em
 * novas contratações.
 *
 * RELEASE: 2026-08-19 v1.0.0 - Versão inicial.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const log = createLogger('useCancelCredit');

export interface CancelCreditParams {
  /** ID do crédito a ser cancelado */
  creditoId: string;
  /** Motivo obrigatório (5 a 1000 caracteres) */
  motivo: string;
  /** ID do cliente — usado apenas para invalidar o cache do histórico */
  customerId?: string | null;
}

export function useCancelCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creditoId, motivo }: CancelCreditParams) => {
      const { data, error } = await supabase.rpc('cancel_financeiro_credito', {
        p_credito_id: creditoId,
        p_motivo: motivo,
      });

      if (error) {
        log.error('Falha ao cancelar crédito', error);
        throw error;
      }

      return data as { success: boolean; credito_id: string; saldo_cancelado: number };
    },
    onSuccess: (_data, variables) => {
      toast.success('Crédito cancelado com sucesso');
      // Invalida o histórico de créditos do cliente e listagens financeiras relacionadas.
      queryClient.invalidateQueries({ queryKey: ['customer-credit-history'] });
      if (variables.customerId) {
        queryClient.invalidateQueries({ queryKey: ['customer-modal-data', variables.customerId] });
      }
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Não foi possível cancelar o crédito');
    },
  });
}
